import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { catchError, finalize, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import InputActions from '../../actions/generations/input.actions';
import { UploadAttachmentService } from 'src/app/services/upload-attachment.service';
import { environment } from 'src/environments/environment';
import { faNodeType } from 'src/app/pages/gathering/file-attachments/file-attachments.component';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { userData } from '../../selectors/auth.selector';

@Injectable()
export class InputEffects {
  private readonly actions = inject(Actions);
  private readonly httpService = inject(HttpBaseService);
  private readonly loaderService = inject(AppLoaderService);
  private readonly uploadService = inject(UploadAttachmentService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly store = inject(Store);

  getAllFiles$ = createEffect(() =>
    this.actions.pipe(
      ofType(InputActions.getAllFiles),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { urlPathVariables } = action;
        const pathVariablesCopy: Record<string, unknown> = { ...urlPathVariables };
        if (!pathVariablesCopy['appId']) {
          pathVariablesCopy['appId'] = this.appGlobalService.get('appId') as string;
        }
        const apiConstants = {
          method: 'get',
          url: 're/wizard/{appId}/{generationId}/fetch-files'
        };
        return this.httpService[apiConstants.method](apiConstants, pathVariablesCopy).pipe(
          map((response: any) =>
            InputActions.getAllFilesSuccess({
              files: response.files,
              attachmentOrderingInfoSid: response.attachmentOrderingInfoSid
            })
          ),
          finalize(() => {
            this.loaderService.hide();
          }),
          catchError((error) => {
            console.error('Error fetching files:', error);
            return of(InputActions.getAllFilesFailure());
          })
        );
      })
    )
  );

  createFile$ = createEffect(() =>
    this.actions.pipe(
      ofType(InputActions.uploadNewFile),
      tap(() => {
        this.loaderService.show();
      }),
      withLatestFrom(this.store.select(userData)),
      switchMap(([action, user]: any) => {
        const { files, attachmentOrderingInfoSid, generationId } = action;
        const url = `${environment.apihost}/appfileattachments/attachment/upload`;
        const userName = user
          ? user['firstName'] + ' ' + user['lastName']
          : 'Unknown User';
        const fileBasicData = {
          type: faNodeType.UPLOAD,
          folder: false,
          data: {
            owner: userName,
            date: new Date(),
            status: 'Draft'
          }
        };
        return this.uploadService.uploadFile(files, url, '', attachmentOrderingInfoSid, '', fileBasicData).pipe(
          map((_: any) => {
            this.httpService.showMessage('success', 'File uploaded successfully');
            return InputActions.getAllFiles({
              urlPathVariables: {
                generationId: generationId
              }
            });
          }),
          finalize(() => {
            this.loaderService.hide();
          }),
          catchError((error) => {
            this.httpService.showMessage('error', 'File upload failed');
            return of(error);
          })
        );
      })
    )
  );

  updateSelectedFiles$ = createEffect(() =>
    this.actions.pipe(
      ofType(InputActions.updateSelectedFiles),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const payload: Record<string, unknown> = { ...action };
        delete payload.type;
        const apiConstants = {
          method: 'put',
          url: 're/wizard/update-files-selected'
        };
        return this.httpService[apiConstants.method](apiConstants, payload).pipe(
          map((_: any) => {
            if (Object.keys(_).length === 0) {
              return InputActions.updateSelectedFilesSuccess();
            } else {
              return InputActions.updateSelectedFilesFailure();
            }
          }),
          finalize(() => {
            this.loaderService.hide();
          }),
          catchError((error) => {
            console.error('Error updating selected files:', error);
            return of(InputActions.updateSelectedFilesFailure());
          })
        );
      })
    )
  );
}
